import { ApplicationConfig, provideExperimentalZonelessChangeDetection, importProvidersFrom, APP_INITIALIZER, isDevMode } from '@angular/core';
import { provideRouter, withViewTransitions } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideServiceWorker } from '@angular/service-worker';
import { AppStore, PortalUpdateService, PortalInstallService } from '@kanek/shared-data-access';
import { GamePluginRegistry, WheelPlugin, ScratchcardPlugin, GameBoardPlugin, DiceRollPlugin, PollPlugin, SportsPlugin } from '@kanektech.com/game-engine';
import { IonicStorageModule } from '@ionic/storage-angular';
import { appRoutes } from './app.routes';

/**
 * Initialize game engine plugins
 * Registers all available game type plugins with the GamePluginRegistry singleton.
 * Checks for existing plugins to prevent duplicate registration during HMR (Hot Module Replacement).
 * @param registry - GamePluginRegistry singleton instance
 * @returns Initialization function for APP_INITIALIZER
 */
function initializeGameEngine(registry: GamePluginRegistry) {
  return () => {
    // Register game plugins
    if (!registry.hasPlugin('wheel')) {
      registry.register(new WheelPlugin());
    }
    if (!registry.hasPlugin('scratchcard')) {
      registry.register(new ScratchcardPlugin());
    }
    if (!registry.hasPlugin('game-board')) {
      registry.register(new GameBoardPlugin());
    }
    if (!registry.hasPlugin('dice-roll')) {
      registry.register(new DiceRollPlugin());
    }
    if (!registry.hasPlugin('poll')) {
      registry.register(new PollPlugin());
    }
    if (!registry.hasPlugin('sports')) {
      registry.register(new SportsPlugin());
    }
    console.log('[Portal] Game engine initialized');
  };
}

/**
 * Initialize PWA services
 * Ensures the services are instantiated and start monitoring for updates/installation
 * @param updateService - PortalUpdateService singleton instance
 * @param installService - PortalInstallService singleton instance
 * @returns Initialization function for APP_INITIALIZER
 */
function initializePWA(updateService: PortalUpdateService, installService: PortalInstallService) {
  return () => {
    console.log('[Portal] PWA update and install services initialized');
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes, withViewTransitions()),
    provideAnimationsAsync(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializePWA,
      deps: [PortalUpdateService, PortalInstallService],
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideExperimentalZonelessChangeDetection(),
    provideNativeDateAdapter(),
    importProvidersFrom(IonicStorageModule.forRoot()),
    AppStore,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeGameEngine,
      deps: [GamePluginRegistry],
      multi: true,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'never',
        subscriptSizing: 'dynamic',
      },
    },
  ],
};
